import { lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const Landing = lazy(() => import("@/pages/Landing"));

const LandingFallback = () => (
  <div className="min-h-screen bg-zinc-900 text-white">
    <div className="border-b border-zinc-800 px-4 py-4 sm:px-6">
      <span className="text-lg font-semibold">AudioTrack</span>
    </div>
    <div
      aria-label="Loading AudioTrack"
      className="mx-auto flex max-w-3xl animate-pulse flex-col items-center gap-4 px-6 pt-20"
    >
      <div className="h-12 w-full max-w-xl rounded bg-zinc-800" />
      <div className="h-5 w-full max-w-2xl rounded bg-zinc-800" />
      <div className="h-5 w-2/3 max-w-xl rounded bg-zinc-800" />
    </div>
  </div>
);

export default function RootRedirect() {
  const { user, userDetails, loading, isAdmin, adminLoading } = useAuth();

  if (loading || adminLoading || (user && !userDetails)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (user) {
    if (isAdmin) {
      const previewCreatorId = localStorage.getItem("admin-mock-creator-id");
      if (previewCreatorId) {
        return (
          <Navigate
            to={`/marketplace?previewCreatorId=${encodeURIComponent(previewCreatorId)}`}
            replace
          />
        );
      }
    }

    // Admin authorization is independent from the profile's product role.
    // A creator-typed admin must never be stranded in creator-only routing.
    if (isAdmin && userDetails?.user_type === "creator") {
      return <Navigate to="/admin" replace />;
    }

    // Redirect based on user type
    if (userDetails?.user_type === "creator") {
      return <Navigate to="/marketplace" replace />;
    } else if (userDetails?.user_type === "marketer") {
      return <Navigate to="/dashboard" replace />;
    }
    // Unknown type: send to auth instead of forcing a marketer dashboard fallback.
    return <Navigate to="/auth" replace />;
  }

  return (
    <Suspense fallback={<LandingFallback />}>
      <Landing />
    </Suspense>
  );
}
